// Angular and RJX Imports
// =========================================================
import { ChangeDetectorRef, Component, type OnInit, type OnDestroy, NgZone } from '@angular/core'
import { v4 as uuidv4 } from 'uuid'
import { NavigationEnd, NavigationStart, Router, RoutesRecognized } from '@angular/router'
// Amplify / Log Rocket Imports
// =========================================================
import { AuthenticatorService } from '@aws-amplify/ui-angular'
import LogRocket from 'logrocket'
// Custom Imports
// =========================================================

import { DynamicDialogRef, DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog'
import {
  BehaviorSubject,
  filter,
  pairwise,
  fromEvent,
  merge,
  timer,
  interval,
  type Observable,
  type Subscription
} from 'rxjs'
import { CognitoService } from './core/services/auth/auth-cognito.service'
import { environment } from 'src/environments/environment'

import { PrimeNgOverlayPanelFixService } from './core/services/overlay.service'

import { switchMap, take, skipWhile, tap } from 'rxjs/operators'
import { RouterHistoryService } from './core/services/router-history-service'
import initHelpHero from 'helphero'

if (environment.production) {
  LogRocket.init('sb46sp/sizeo-9wlvy', {
    console: {
      isEnabled: true
    }
  })
}

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [DialogService]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'sizeo-v2'
  private readonly routerEventsLogSubscription: Subscription
  private readonly routerNavigationEndSubscription: Subscription

  //  routing variables
  previousUrlViaNavigationEnd$ = new BehaviorSubject<string | null>(null)
  currentUrlViaNavigationEnd$ = new BehaviorSubject<string | null>(null)
  previousUrlViaRoutesRecognized$ = new BehaviorSubject<string | null>(null)
  currentUrlViaRoutesRecognized$ = new BehaviorSubject<string | null>(null)

  // Via RouterHistoryService
  previousUrlViaRouterHistoryService$ = this.routerHistoryService.previousUrl$

  currentUrlViaRouterHistoryService$ = this.routerHistoryService.currentUrl$

  //  authentication variables
  user: any
  isCheckingAuth: boolean = true
  logs: Array<{ event: string, message: string }> = []

  constructor (
    public router: Router,
    public authenticator: AuthenticatorService,
    public primeNgOverlayPanelFixService: PrimeNgOverlayPanelFixService,
    public cognitoService: CognitoService,
    public ref: DynamicDialogRef,
    public dialogService: DialogService,
    public config: DynamicDialogConfig,
    public _ngZone: NgZone,
    public _cd: ChangeDetectorRef,
    private readonly routerHistoryService: RouterHistoryService
  ) {
    // Event logging only
    this.routerEventsLogSubscription = router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.logs.push({
          event: 'NavigationStart',
          message: JSON.stringify(event)
        })
      }
      if (event instanceof NavigationEnd) {
        this.logs.push({
          event: 'NavigationEnd',
          message: JSON.stringify(event)
        })
      }
    })
    // Via Navigation End Event
    this.currentUrlViaNavigationEnd$.next(router.url)
    this.routerNavigationEndSubscription = router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.previousUrlViaNavigationEnd$.next(this.currentUrlViaNavigationEnd$.value)
        this.currentUrlViaNavigationEnd$.next(event.urlAfterRedirects)
      }
    })
    // Via RoutesRecognized
    router.events
      .pipe(
        filter(evt => evt instanceof RoutesRecognized),
        pairwise()
      )
      .subscribe(([previous, current]: [RoutesRecognized, RoutesRecognized]) => {
        this.previousUrlViaRoutesRecognized$.next(previous.urlAfterRedirects)
        this.currentUrlViaRoutesRecognized$.next(current.urlAfterRedirects)
      })
    this.cognitoService.triggerUserUpdate$.subscribe(user => {
      console.log('---> User Details: ', user)
      this.isCheckingAuth = false

      if (this.user !== user) {
        this.user = user
      }
      if (!this.user) {
        this.router.navigateByUrl('/')
        this.cognitoService.authFederatedSignIn()
      }

      // Configure User Details for Log Rocket
      if (environment.production) {
        LogRocket.identify(uuidv4(), {
          name: user.sub,
          email: user.email
        })
      }

      // Defer detectChanges one tick so it runs after the current CD cycle,
      // avoiding ExpressionChangedAfterItHasBeenChecked in dev mode.
      setTimeout(() => this._cd.detectChanges())
    })

    const stage = environment.stage
    if (stage === 'v2demo') {
      const hlp = initHelpHero('TCYlTXOZGi')
      hlp.anonymous()
    }
    this.cognitoService.hubListener()
    // In Amplify v6 the 'configured' Hub event was removed, so we trigger the
    // initial auth check explicitly instead of relying on Hub to start the flow.
    this.cognitoService.checkUser()
  }

  ngOnDestroy () {
    this.routerEventsLogSubscription?.unsubscribe()
    this.routerNavigationEndSubscription?.unsubscribe()
  }

  ngOnInit () {
    // Initialize LogRocket --> Then update the user's details once they are authorized through amplify/cognito
    if (environment.production) {
      LogRocket.identify(uuidv4(), {
        name: 'dev-user'
      })
    }
  }
}
